import React, { useCallback } from "react";

import type { FeToolParameter, FeToolSchema } from "@/services/agent";
import type { OsAniContextSnapshot } from "@/components/ui/os-layout/buildOsAniContextSnapshot";
import {
  buildRefSuggestions,
  resolveParamFieldKind,
  toolHasNoParams,
  type FeToolDebugPickerOptions,
  type RefSuggestion,
} from "./toolForms";

const fieldClass =
  "w-full rounded-lg border border-slate-600/60 bg-slate-900/40 px-2 py-1.5 text-sm text-slate-100";
const labelClass = "text-[11px] font-medium uppercase tracking-wide text-slate-500";

export interface FeToolDebugFormProps {
  schema: FeToolSchema;
  params: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  osContext?: OsAniContextSnapshot;
  feOpenAppAllowlist?: readonly string[];
  pickerOptions?: FeToolDebugPickerOptions;
}

function ParamLabel({ param }: { param: FeToolParameter }) {
  return (
    <span className={labelClass}>
      {param.name}
      {param.required ? "" : " (optional)"}
    </span>
  );
}

function DefaultParamField({
  param,
  value,
  enumValues,
  onPatch,
}: {
  param: FeToolParameter;
  value: unknown;
  enumValues?: string[];
  onPatch: (value: unknown) => void;
}) {
  if (param.type === "boolean") {
    return (
      <label className="flex items-center gap-2 text-xs text-slate-300">
        <input
          type="checkbox"
          className="rounded border-slate-600"
          checked={value === true}
          onChange={(event) => onPatch(event.target.checked)}
          data-testid={`fe-tool-debug-field-${param.name}`}
        />
        <span>{param.description || param.name}</span>
      </label>
    );
  }

  if (enumValues && enumValues.length > 0) {
    return (
      <select
        className={fieldClass}
        value={String(value ?? "")}
        onChange={(event) => onPatch(event.target.value)}
        data-testid={`fe-tool-debug-field-${param.name}`}
      >
        {!param.required ? <option value="">—</option> : null}
        {enumValues.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    );
  }

  if (param.type === "number") {
    return (
      <input
        type="number"
        className={fieldClass}
        value={value === undefined || value === null ? "" : String(value)}
        onChange={(event) => {
          const raw = event.target.value;
          onPatch(raw === "" ? undefined : Number(raw));
        }}
        data-testid={`fe-tool-debug-field-${param.name}`}
      />
    );
  }

  return (
    <input
      type="text"
      className={fieldClass}
      value={value === undefined || value === null ? "" : String(value)}
      onChange={(event) => onPatch(event.target.value)}
      data-testid={`fe-tool-debug-field-${param.name}`}
    />
  );
}

function JsonObjectField({
  param,
  value,
  onPatch,
}: {
  param: FeToolParameter;
  value: unknown;
  onPatch: (value: unknown) => void;
}) {
  const text =
    value === undefined
      ? ""
      : typeof value === "string"
        ? value
        : JSON.stringify(value, null, 2);

  return (
    <textarea
      className={`${fieldClass} min-h-[72px] font-mono text-xs`}
      value={text}
      spellCheck={false}
      onChange={(event) => {
        const raw = event.target.value.trim();
        if (!raw) {
          onPatch(undefined);
          return;
        }
        try {
          onPatch(JSON.parse(raw) as unknown);
        } catch {
          onPatch(event.target.value);
        }
      }}
      data-testid={`fe-tool-debug-field-${param.name}`}
    />
  );
}

function RefPickerField({
  param,
  value,
  suggestions,
  onPatch,
}: {
  param: FeToolParameter;
  value: unknown;
  suggestions: RefSuggestion[];
  onPatch: (value: unknown) => void;
}) {
  const listId = `fe-tool-debug-${param.name}-suggestions`;
  return (
    <>
      <input
        type="text"
        className={fieldClass}
        list={listId}
        placeholder="portfolio:port-1/holdings"
        value={value === undefined || value === null ? "" : String(value)}
        onChange={(event) => onPatch(event.target.value)}
        data-testid={`fe-tool-debug-field-${param.name}`}
      />
      <datalist id={listId}>
        {suggestions.map((item) => (
          <option key={item.ref} value={item.ref}>
            {item.label}
          </option>
        ))}
      </datalist>
    </>
  );
}

export function FeToolDebugForm({
  schema,
  params,
  onChange,
  osContext,
  feOpenAppAllowlist,
  pickerOptions,
}: FeToolDebugFormProps) {
  const patchParam = useCallback(
    (name: string, value: unknown) => {
      const next = { ...params, [name]: value };
      if (value === undefined) {
        delete next[name];
      }
      onChange(next);
    },
    [onChange, params],
  );

  if (toolHasNoParams(schema)) {
    return <p className="text-[11px] text-slate-500">This tool has no parameters.</p>;
  }

  const refSuggestions = buildRefSuggestions(osContext, pickerOptions ?? { artifacts: [], reviewTasks: [] }, feOpenAppAllowlist);

  return (
    <div className="space-y-3" data-testid="fe-tool-debug-form">
      {schema.parameters.map((param) => {
        const kind = resolveParamFieldKind(param);
        return (
          <label key={param.name} className="block space-y-1">
            <ParamLabel param={param} />
            {param.description ? (
              <p className="text-[10px] text-slate-500">{param.description}</p>
            ) : null}
            {kind === "ref_picker" ? (
              <RefPickerField
                param={param}
                value={params[param.name]}
                suggestions={refSuggestions}
                onPatch={(value) => patchParam(param.name, value)}
              />
            ) : kind === "json_object" ? (
              <JsonObjectField
                param={param}
                value={params[param.name]}
                onPatch={(value) => patchParam(param.name, value)}
              />
            ) : (
              <DefaultParamField
                param={param}
                value={params[param.name]}
                enumValues={param.enum}
                onPatch={(value) => patchParam(param.name, value)}
              />
            )}
          </label>
        );
      })}
    </div>
  );
}
